import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiSearch, FiCheck, FiX } from "react-icons/fi";
import { collection, doc, getDocs, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../../../../firebaseConfig";
import "./StudentTestTab.css";

function accountInitials(name, email) {
  const source = String(name || email || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const MANUAL_LIST_LIMIT = 50;

function normalizeNameExact(value) {
  return String(value || "").trim();
}

function normalizeNameForCompare(value) {
  return normalizeNameExact(value).toLowerCase().replace(/\s+/g, " ");
}

// ── Lightweight name-similarity scoring for "Did you mean?" suggestions ──
function normalizeForScore(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function levenshtein(a, b) {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  let curr = new Array(n + 1);
  for (let i = 1; i <= m; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

function nameSimilarity(aRaw, bRaw) {
  const a = normalizeForScore(aRaw);
  const b = normalizeForScore(bRaw);
  if (!a || !b) return 0;

  const maxLen = Math.max(a.length, b.length) || 1;
  const editRatio = 1 - levenshtein(a, b) / maxLen;

  const ta = new Set(a.split(" ").filter(Boolean));
  const tb = new Set(b.split(" ").filter(Boolean));
  let inter = 0;
  ta.forEach((t) => {
    if (tb.has(t)) inter += 1;
  });
  const union = new Set([...ta, ...tb]).size || 1;
  const tokenJaccard = inter / union;

  return Math.max(editRatio, tokenJaccard);
}

function detectUid(docId, data) {
  const sid = String(data?.studentId || "").trim();
  if (sid) return sid;
  const match = String(docId || "").match(/^([^_]+)_(act|sat)_diagnostic_test/i);
  return match?.[1] || "";
}

function detectType(docId, data) {
  const type = String(data?.type || "").trim().toUpperCase();
  if (type === "ACT") return "ACT";
  if (type === "SAT") return "SAT";
  const id = String(docId || "").toLowerCase();
  if (id.includes("_act_")) return "ACT";
  return "SAT";
}

function createEmptyDiagnosticMeta() {
  return {
    satCount: 0,
    actCount: 0,
  };
}

function toDateMaybe(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "object" && typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(value) {
  const d = toDateMaybe(value);
  if (!d) return "—";
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function buildAttemptName(type, data) {
  return (
    data?.testTitle ||
    data?.testName ||
    data?.displayName ||
    (data?.testId ? `${type} Diagnostic (${data.testId})` : `${type} Diagnostic`)
  );
}

function getAttemptScore(type, data) {
  if (type === "ACT") {
    return data?.compositeScore ?? data?.composite ?? data?.score ?? null;
  }
  return (
    data?.totalScore ??
    data?.totalScaled ??
    data?.totalScaledScore ??
    data?.score ??
    null
  );
}

function isAttemptCompleted(data) {
  return data?.status === "completed" || !!data?.completedAt;
}

function reportUrlFor(attempt) {
  const base = attempt.type === "ACT" ? "/diagnostics/act/report" : "/diagnostics/sat/report";
  const params = new URLSearchParams({ studentId: attempt.uid, attemptId: attempt.attemptId });
  return `${base}?${params.toString()}`;
}

export default function StudentTestTab({ student, permissions }) {
  const canLink = permissions ? !!permissions.linkTest : true;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [usersByUid, setUsersByUid] = useState({});
  const [diagnosticMetaByUid, setDiagnosticMetaByUid] = useState({});
  const [attemptsByUid, setAttemptsByUid] = useState({});
  const [exactMatches, setExactMatches] = useState([]);
  const [resolvedUidFromPicker, setResolvedUidFromPicker] = useState("");
  const [showManualFallback, setShowManualFallback] = useState(false);
  const [manualQuery, setManualQuery] = useState("");
  const [manualSelectedUid, setManualSelectedUid] = useState("");
  const [manualSelectedUser, setManualSelectedUser] = useState(null);
  const [renameUserOnLink, setRenameUserOnLink] = useState(true);
  const [linking, setLinking] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const autoPersistRef = useRef("");

  const studentId = String(student?.id || "");
  const studentName = normalizeNameExact(student?.name);
  const studentNameForCompare = normalizeNameForCompare(student?.name);
  const linkedUid = String(student?.diagnosticUid || "").trim();

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError("");
      try {
        const [usersSnap, diagSnap] = await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(collection(db, "diagnostic_results")),
        ]);
        if (cancelled) return;

        const nextUsersByUid = {};
        usersSnap.forEach((userDoc) => {
          const data = userDoc.data() || {};
          nextUsersByUid[userDoc.id] = {
            uid: userDoc.id,
            name: normalizeNameExact(data?.name),
            email: String(data?.email || "").trim(),
            role: String(data?.role || "").trim().toLowerCase(),
          };
        });

        const nextMetaByUid = {};
        const nextAttemptsByUid = {};
        diagSnap.forEach((diagDoc) => {
          const data = diagDoc.data() || {};
          if (data?.isArchived) return;

          const uid = detectUid(diagDoc.id, data);
          if (!uid) return;

          const type = detectType(diagDoc.id, data);
          const completed = isAttemptCompleted(data);
          const dateValue = data?.completedAt || data?.completed_on || data?.completedAtIso || null;

          // Per-uid attempt summaries (used for the report list).
          const list = nextAttemptsByUid[uid] || (nextAttemptsByUid[uid] = []);
          list.push({
            attemptId: diagDoc.id,
            uid,
            type,
            name: buildAttemptName(type, data),
            score: getAttemptScore(type, data),
            status: completed ? "Completed" : "In Progress",
            completed,
            dateLabel: formatDate(dateValue),
            dateMs: toDateMaybe(dateValue)?.getTime() || 0,
          });

          // Completed counts (used by the exact-match picker + auto-link).
          if (!completed) return;
          const meta = nextMetaByUid[uid] || createEmptyDiagnosticMeta();
          if (type === "ACT") {
            meta.actCount += 1;
          } else {
            meta.satCount += 1;
          }
          nextMetaByUid[uid] = meta;
        });

        const matches = Object.values(nextUsersByUid).filter((user) => {
          return normalizeNameForCompare(user.name) === studentNameForCompare;
        });

        setUsersByUid(nextUsersByUid);
        setDiagnosticMetaByUid(nextMetaByUid);
        setAttemptsByUid(nextAttemptsByUid);
        setExactMatches(matches);
      } catch (loadErr) {
        if (!cancelled) {
          setError(loadErr?.message || "Failed to load diagnostic data.");
          setUsersByUid({});
          setDiagnosticMetaByUid({});
          setAttemptsByUid({});
          setExactMatches([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, [studentNameForCompare, refreshKey]);

  useEffect(() => {
    setResolvedUidFromPicker("");
    setManualSelectedUid("");
    setManualSelectedUser(null);
    setManualQuery("");
    setShowManualFallback(false);
    setRenameUserOnLink(true);
    autoPersistRef.current = "";
  }, [studentId]);

  const hasLinkedUid = linkedUid.length > 0;
  const hasOneExactMatch = !hasLinkedUid && exactMatches.length === 1;
  const hasMultipleExactMatches = !hasLinkedUid && !resolvedUidFromPicker && exactMatches.length > 1;

  const resolvedUid = useMemo(() => {
    if (hasLinkedUid) return linkedUid;
    if (resolvedUidFromPicker) return resolvedUidFromPicker;
    if (hasOneExactMatch) return exactMatches[0].uid;
    return "";
  }, [hasLinkedUid, linkedUid, resolvedUidFromPicker, hasOneExactMatch, exactMatches]);

  const resolvedUser = resolvedUid ? usersByUid[resolvedUid] || null : null;

  const resolvedAttempts = useMemo(() => {
    const list = resolvedUid ? attemptsByUid[resolvedUid] || [] : [];
    return [...list].sort((a, b) => b.dateMs - a.dateMs);
  }, [resolvedUid, attemptsByUid]);

  const hasAnyReports = resolvedAttempts.length > 0;

  // Only student-role accounts are linkable report accounts.
  const studentUsers = useMemo(
    () =>
      Object.values(usersByUid)
        .filter((u) => u.role === "student")
        .map((u) => ({ uid: u.uid, name: u.name, email: u.email }))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [usersByUid]
  );

  // "Did you mean?" — likely matches when the name is close but not exact.
  const suggestions = useMemo(() => {
    if (!studentName) return [];
    return studentUsers
      .map((u) => ({ user: u, score: nameSimilarity(studentName, u.name) }))
      .filter((x) => x.score >= 0.45)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((x) => x.user);
  }, [studentName, studentUsers]);

  // Searchable account list for the manual link picker.
  const manualMatches = useMemo(() => {
    const q = manualQuery.trim().toLowerCase();
    if (!q) return studentUsers;
    return studentUsers.filter((u) => {
      const name = String(u.name || "").toLowerCase();
      const email = String(u.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [manualQuery, studentUsers]);

  const visibleManualMatches = manualMatches.slice(0, MANUAL_LIST_LIMIT);
  const manualOverflow = manualMatches.length - visibleManualMatches.length;

  useEffect(() => {
    if (!canLink) return;
    if (!hasOneExactMatch || hasLinkedUid) return;
    if (!studentId) return;
    const uid = exactMatches[0]?.uid;
    if (!uid) return;
    const meta = diagnosticMetaByUid[uid] || createEmptyDiagnosticMeta();
    if (!meta.satCount && !meta.actCount) return;
    if (autoPersistRef.current === `${studentId}:${uid}`) return;

    async function persistAutoLink() {
      try {
        await updateDoc(doc(db, "students", studentId), {
          diagnosticUid: uid,
          diagnosticMatchName: exactMatches[0]?.name || "",
          diagnosticLinkSource: "exact_name_auto",
          diagnosticLinkedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch {
        // Keep this silent to avoid interrupting the report-first flow.
      } finally {
        autoPersistRef.current = `${studentId}:${uid}`;
      }
    }
    persistAutoLink();
  }, [canLink, hasOneExactMatch, hasLinkedUid, exactMatches, diagnosticMetaByUid, studentId]);

  const persistStudentLink = async (uid, source) => {
    if (!studentId || !uid) return;
    await updateDoc(doc(db, "students", studentId), {
      diagnosticUid: uid,
      diagnosticMatchName: usersByUid[uid]?.name || "",
      diagnosticLinkSource: source,
      diagnosticLinkedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const handleSelectExactMatch = async (uid) => {
    setLinking(true);
    setError("");
    try {
      await persistStudentLink(uid, "exact_name_manual_pick");
      setResolvedUidFromPicker(uid);
      setShowManualFallback(false);
    } catch (saveErr) {
      setError(saveErr?.message || "Failed to save selected account link.");
    } finally {
      setLinking(false);
    }
  };

  const handleConfirmManualLink = async () => {
    if (!manualSelectedUid) return;
    setLinking(true);
    setError("");
    try {
      if (renameUserOnLink) {
        await updateDoc(doc(db, "users", manualSelectedUid), {
          name: studentName,
          updatedAt: serverTimestamp(),
        });
      }

      await persistStudentLink(manualSelectedUid, "manual_confirmed");
      setResolvedUidFromPicker(manualSelectedUid);
      setShowManualFallback(false);
      setRefreshKey((prev) => prev + 1);
    } catch (saveErr) {
      setError(saveErr?.message || "Failed to confirm manual link.");
    } finally {
      setLinking(false);
    }
  };

  const openReport = (attempt) => {
    if (!attempt?.completed) return;
    window.open(reportUrlFor(attempt), "_blank", "noopener");
  };

  const selectManualAccount = (user) => {
    if (!user) return;
    setManualSelectedUid(user.uid);
    setManualSelectedUser(user);
  };

  const shouldShowManualFallback =
    canLink && (showManualFallback || !resolvedUid || !hasAnyReports || hasMultipleExactMatches);

  if (loading) {
    return (
      <div className="student-test-tab">
        <div className="sdw-empty">Loading diagnostic reports…</div>
      </div>
    );
  }

  return (
    <div className="student-test-tab">
      {error && <div className="student-test-tab__error">{error}</div>}

      {hasMultipleExactMatches && (
        <section className="sdw-section">
          <div className="sdw-section__head">
            <div className="sdw-section__title">Multiple Matching Accounts</div>
          </div>
          <p className="student-test-tab__subtext">
            More than one account matches <strong>{studentName || "this student"}</strong>.{" "}
            {canLink ? "Choose the one to use." : "Ask an admin to link the correct account."}
          </p>
          <div className="student-test-tab__match-list">
            {exactMatches.map((entry) => {
              const meta = diagnosticMetaByUid[entry.uid] || createEmptyDiagnosticMeta();
              return (
                <div key={entry.uid} className="student-test-tab__match-item">
                  <div>
                    <div className="student-test-tab__match-name">{entry.name || "Unknown"}</div>
                    <div className="student-test-tab__match-meta">
                      {entry.email || "No email"} · SAT {meta.satCount} · ACT {meta.actCount}
                    </div>
                  </div>
                  {canLink && (
                    <button
                      type="button"
                      className="sdw-btn sdw-btn--primary sdw-btn--sm"
                      onClick={() => handleSelectExactMatch(entry.uid)}
                      disabled={linking}
                    >
                      {linking ? "Saving…" : "Use This Account"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {resolvedUid && hasAnyReports && (
        <section className="sdw-section">
          <div className="sdw-section__head">
            <div className="sdw-section__title">Diagnostic Reports</div>
            <div className="sdw-section__sub">
              {resolvedUser?.name || resolvedUser?.email || "Linked account"} · {resolvedAttempts.length} attempt
              {resolvedAttempts.length !== 1 ? "s" : ""}
            </div>
          </div>

          <div className="student-test-tab__table-wrap">
            <table className="student-test-tab__table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Attempt</th>
                  <th>Completed</th>
                  <th className="student-test-tab__col-score">Score</th>
                  <th>Status</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {resolvedAttempts.map((attempt) => (
                  <tr
                    key={attempt.attemptId}
                    className={`student-test-tab__row ${attempt.completed ? "is-clickable" : "is-disabled"}`}
                    onClick={() => openReport(attempt)}
                    title={attempt.completed ? "Open report in a new tab" : "Attempt not completed"}
                  >
                    <td>
                      <span className={`student-test-tab__type student-test-tab__type--${attempt.type.toLowerCase()}`}>
                        {attempt.type}
                      </span>
                    </td>
                    <td className="student-test-tab__attempt-name">{attempt.name}</td>
                    <td className="student-test-tab__muted">{attempt.dateLabel}</td>
                    <td className="student-test-tab__col-score student-test-tab__score">
                      {attempt.score == null ? "—" : attempt.score}
                    </td>
                    <td>
                      <span
                        className={`student-test-tab__status student-test-tab__status--${
                          attempt.completed ? "done" : "progress"
                        }`}
                      >
                        {attempt.status}
                      </span>
                    </td>
                    <td className="student-test-tab__open">
                      {attempt.completed ? "Open ↗" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {canLink && (
            <div className="student-test-tab__list-foot">
              <button
                type="button"
                className="student-test-tab__link-btn"
                onClick={() => setShowManualFallback((prev) => !prev)}
              >
                {showManualFallback ? "Hide manual link tool" : "Wrong account? Link a different one"}
              </button>
            </div>
          )}
        </section>
      )}

      {resolvedUid && !hasAnyReports && (
        <section className="sdw-section">
          <div className="sdw-section__head">
            <div className="sdw-section__title">No Diagnostic Reports</div>
          </div>
          <p className="student-test-tab__subtext">
            The linked account ({resolvedUser?.name || resolvedUid}) has no diagnostic results yet.
            {canLink ? " Use the manual tool below to link a different account if needed." : ""}
          </p>
        </section>
      )}

      {!resolvedUid && !hasMultipleExactMatches && (
        <section className="sdw-section">
          <div className="sdw-section__head">
            <div className="sdw-section__title">No Exact Match Found</div>
          </div>
          <p className="student-test-tab__subtext">
            No report account exactly matches <strong>{studentName || "this student"}</strong>.
            {canLink
              ? " Use the manual tool below to find and link the correct account."
              : " Ask an admin to link the correct diagnostic account."}
          </p>
        </section>
      )}

      {shouldShowManualFallback && (
        <section className="sdw-section">
          <div className="sdw-section__head">
            <div className="sdw-section__title">Manual Link Tool</div>
          </div>

          <p className="student-test-tab__subtext">
            Search student accounts and link the correct one to this profile. After linking, that account’s
            diagnostic reports will appear here.
          </p>

          {suggestions.length > 0 && (
            <div className="stl-suggest">
              <div className="stl-suggest__label">Suggested matches</div>
              <div className="stl-suggest__list">
                {suggestions.map((u) => (
                  <button
                    key={u.uid}
                    type="button"
                    className={`stl-suggest__chip ${manualSelectedUid === u.uid ? "is-active" : ""}`}
                    onClick={() => selectManualAccount(u)}
                  >
                    <span className="stl-suggest__name">{u.name || "Unknown"}</span>
                    {u.email && <span className="stl-suggest__email">{u.email}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="stl-picker">
            <div className="stl-search">
              <FiSearch className="stl-search__icon" size={15} aria-hidden="true" />
              <input
                className="stl-search__input"
                value={manualQuery}
                onChange={(e) => setManualQuery(e.target.value)}
                placeholder="Search by name or email…"
                aria-label="Search student accounts"
              />
              {manualQuery && (
                <button
                  type="button"
                  className="stl-search__clear"
                  onClick={() => setManualQuery("")}
                  aria-label="Clear search"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>

            <div className="stl-list" role="listbox" aria-label="Student accounts">
              {visibleManualMatches.length === 0 ? (
                <div className="stl-list__empty">No accounts match “{manualQuery.trim()}”.</div>
              ) : (
                visibleManualMatches.map((u) => {
                  const selected = manualSelectedUid === u.uid;
                  return (
                    <button
                      key={u.uid}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`stl-option ${selected ? "is-selected" : ""}`}
                      onClick={() => selectManualAccount(u)}
                    >
                      <span className="stl-option__avatar">{accountInitials(u.name, u.email)}</span>
                      <span className="stl-option__text">
                        <span className="stl-option__name">{u.name || "Unnamed account"}</span>
                        <span className="stl-option__email">{u.email || "No email on file"}</span>
                      </span>
                      {selected && <FiCheck className="stl-option__check" size={16} aria-hidden="true" />}
                    </button>
                  );
                })
              )}
            </div>

            {manualOverflow > 0 && (
              <div className="stl-list__foot">
                Showing {visibleManualMatches.length} of {manualMatches.length}. Refine your search to narrow results.
              </div>
            )}
          </div>

          {manualSelectedUid && (
            <div className="stl-confirm">
              <div className="stl-confirm__head">
                <span className="stl-option__avatar stl-option__avatar--lg">
                  {accountInitials(manualSelectedUser?.name, manualSelectedUser?.email)}
                </span>
                <div className="stl-confirm__id">
                  <div className="stl-confirm__name">{manualSelectedUser?.name || manualSelectedUid}</div>
                  <div className="stl-confirm__email">{manualSelectedUser?.email || "No email on file"}</div>
                </div>
              </div>

              <label className="stl-confirm__check">
                <input
                  type="checkbox"
                  checked={renameUserOnLink}
                  onChange={(e) => setRenameUserOnLink(e.target.checked)}
                />
                <span>
                  Update this account’s name to exactly match{" "}
                  <strong>{studentName || "the student name"}</strong>
                </span>
              </label>

              <button
                type="button"
                className="sdw-btn sdw-btn--primary"
                onClick={handleConfirmManualLink}
                disabled={linking}
              >
                {linking ? "Saving…" : "Link This Account"}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
